import { useMutation } from '@tanstack/react-query';

import { MutationConfig } from '@/types/react-query';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ApiMutationFn = ({ params }: { params: any; }) => Promise<any>;

/**
 * A factory function to create a reusable `useMutation` hook for API calls.
 *
 * @param mutationFn The asynchronous function that performs the mutation (e.g., a POST, PUT, DELETE request).
 * @returns A custom React Hook that can be used to trigger the mutation.
 *
 * ---
 *
 * ## Example Usage
 *
 * ### 1. Define your API mutation function:
 *
 * ```ts
 * // src/features/auth/services/login.ts
 * import { post } from '@/libs/http';
 *
 * export const login = ({ params }: { params: LoginCredentials }) => {
 *   return post('/auth/login', params);
 * };
 * ```
 *
 * ### 2. Create the custom mutation hook using the factory:
 *
 * ```ts
 * // src/features/auth/services/login.ts
 * import { createApiMutationHook } from '@/hooks/use-api-mutation';
 *
 * export const useLogin = createApiMutationHook(login);
 * ```
 *
 * ### 3. Use the hook in your component:
 *
 * ```tsx
 * // src/components/login-form.tsx
 * import { useLogin } from '@/features/auth/services/login';
 * import { useCallback, useMemo } from 'react';
 *
 * const LoginForm = () => {
 *   const onSuccess = useCallback((data: LoginResponse) => {
 *     console.log('Login successful!', data);
 *   }, []);
 * 
 *   const onError = useCallback((error: Error) => {
 *     console.error('Login failed:', error);
 *   }, []);
 *
 *   const mutationConfig = useMemo(() => ({
 *     onSuccess,
 *     onError,
 *   }), [onSuccess, onError]);
 *
 *   const { mutate, isLoading } = useLogin({
 *     mutationConfig,
 *   });
 *
 *   const handleSubmit = useCallback((credentials: LoginCredentials) => {
 *     mutate({ params: credentials });
 *   }, [mutate]);
 *
 *   // ... component rendering
 * };
 * ```
 */
export const createApiMutationHook = <TFetcherFn extends ApiMutationFn>(
  mutationFn: TFetcherFn
) => {
  type UseApiMutationOptions = {
    mutationConfig?: MutationConfig<TFetcherFn>;
  };

  const useApiMutation = ({ mutationConfig }: UseApiMutationOptions = {}) => {
    const { onSuccess, ...restConfig } = mutationConfig || {};

    return useMutation({
      onSuccess: (...args) => {
        onSuccess?.(...args);
      },
      ...restConfig,
      mutationFn,
    });
  };

  return useApiMutation;
};
